import { useCallback } from 'react';

import {
	ufoEventProvisioningReadinessLatency,
	ufoEventE2eProvisioningLatency,
	ufoEventE2eProvisioningSystemLatency,
	ufoEventProductSetupSystemLatency,
	ufoEventProductSetupLatency,
} from '@/components/signup/utils/ufo-events';
import { SignupUfoCustomData } from '@/components/signup/utils/ufo.types';

type FinalProvisioningScreen = NonNullable<SignupUfoCustomData['finalProvisioningScreen']>;

/**
 * The combined system metric counts the union of instrumented readiness and unpaused setup
 * intervals. Gaps between those intervals, including render scheduling and redirect-only
 * delays, are excluded; this is not an exhaustive measurement of all system time.
 */
export const useProvisioningLatencyUfo = () => {
	const startE2eProvisioning = useCallback(
		(options: Parameters<typeof ufoEventE2eProvisioningLatency.start>[0]) => {
			void ufoEventE2eProvisioningLatency.start(options);
		},
		[],
	);

	const startProductSetup = useCallback(
		(options: Parameters<typeof ufoEventProductSetupLatency.start>[0]) => {
			void ufoEventProductSetupLatency.start(options);
		},
		[],
	);

	const completeProductSetup = useCallback((finalProvisioningScreen: FinalProvisioningScreen) => {
		void ufoEventProductSetupLatency.success({ customData: { finalProvisioningScreen } });
	}, []);

	const abortE2eProvisioningSystem = useCallback(
		(finalProvisioningScreen?: FinalProvisioningScreen) => {
			if (ufoEventE2eProvisioningSystemLatency.isRunning()) {
				void ufoEventE2eProvisioningSystemLatency.abort({
					customData: { finalProvisioningScreen },
				});
			}
		},
		[],
	);

	const completeE2eProvisioning = useCallback(
		(finalProvisioningScreen?: FinalProvisioningScreen) => {
			void ufoEventE2eProvisioningLatency.success({ customData: { finalProvisioningScreen } });
			// This is a redirect boundary even when a product's setup is still active.
			// The finishing state prevents a later setup callback from emitting twice.
			if (ufoEventE2eProvisioningSystemLatency.isRunning()) {
				if (ufoEventProvisioningReadinessLatency.isRunning()) {
					abortE2eProvisioningSystem(finalProvisioningScreen);
				} else {
					void ufoEventE2eProvisioningSystemLatency.success({
						customData: { finalProvisioningScreen },
					});
				}
			}
		},
		[abortE2eProvisioningSystem],
	);

	const completeAll = useCallback(
		(finalProvisioningScreen: FinalProvisioningScreen) => {
			completeProductSetup(finalProvisioningScreen);
			completeE2eProvisioning(finalProvisioningScreen);
		},
		[completeProductSetup, completeE2eProvisioning],
	);

	const startProvisioningReadiness = useCallback(
		(options: Parameters<typeof ufoEventProvisioningReadinessLatency.start>[0]) => {
			void ufoEventProvisioningReadinessLatency.start(options);
			void ufoEventE2eProvisioningSystemLatency.start(options);
		},
		[],
	);

	const completeProvisioningReadiness = useCallback(
		(finalProvisioningScreen?: FinalProvisioningScreen) => {
			void ufoEventProvisioningReadinessLatency.success({
				customData: { finalProvisioningScreen },
			});
			// Questions may continue after provisioning, before automated setup begins.
			// An animation override can already have started setup; keep counting it.
			if (
				!ufoEventProductSetupSystemLatency.isRunning() ||
				ufoEventProductSetupSystemLatency.isPaused()
			) {
				ufoEventE2eProvisioningSystemLatency.pause();
			}
		},
		[],
	);

	const startProductSetupSystem = useCallback(
		(options: Parameters<typeof ufoEventProductSetupSystemLatency.start>[0]) => {
			const wasSetupPaused =
				ufoEventProductSetupSystemLatency.isRunning() &&
				ufoEventProductSetupSystemLatency.isPaused();
			void ufoEventProductSetupSystemLatency.start(options);
			// A repeated start must preserve a pause for user input, just as UFO does.
			if (!wasSetupPaused) {
				ufoEventE2eProvisioningSystemLatency.resume();
			}
		},
		[],
	);

	const pauseProductSetupSystem = useCallback(() => {
		ufoEventProductSetupSystemLatency.pause();
		// Provisioning still counts when it overlaps input on a setup screen.
		if (!ufoEventProvisioningReadinessLatency.isRunning()) {
			ufoEventE2eProvisioningSystemLatency.pause();
		}
	}, []);

	const resumeProductSetupSystem = useCallback(
		(options: Parameters<typeof ufoEventProductSetupSystemLatency.start>[0]) => {
			ufoEventProductSetupSystemLatency.resume();
			// Start a measurement if none is active.
			// UFO ignores this call when a measurement is already running.
			void ufoEventProductSetupSystemLatency.start(options);
			ufoEventE2eProvisioningSystemLatency.resume();
		},
		[],
	);

	const completeProductSetupSystem = useCallback(
		(finalProvisioningScreen: FinalProvisioningScreen) => {
			void ufoEventProductSetupSystemLatency.success({ customData: { finalProvisioningScreen } });
			if (
				!ufoEventProvisioningReadinessLatency.isRunning() &&
				ufoEventE2eProvisioningSystemLatency.isRunning()
			) {
				void ufoEventE2eProvisioningSystemLatency.success({
					customData: { finalProvisioningScreen },
				});
			}
		},
		[],
	);

	return {
		startE2eProvisioning,
		startProductSetup,
		completeProductSetup,
		completeE2eProvisioning,
		abortE2eProvisioningSystem,
		completeAll,
		startProvisioningReadiness,
		completeProvisioningReadiness,
		startProductSetupSystem,
		pauseProductSetupSystem,
		resumeProductSetupSystem,
		completeProductSetupSystem,
	};
};
